"use client"

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RefreshCw, TestTube, CheckCircle, AlertCircle } from '@/lib/icons'
import { getVersionInfo } from '@/lib/version'

export function VersionTestPanel() {
  const [testResults, setTestResults] = useState<Array<{
    name: string;
    status: 'success' | 'error';
    result?: unknown;
    error?: string;
    duration: number;
    timestamp: string;
  }>>([])
  const [isLoading, setIsLoading] = useState(false)

  const currentVersion = getVersionInfo()

  const runTest = async (testName: string, testFn: () => Promise<unknown>) => {
    setIsLoading(true)
    const startTime = Date.now()
    
    try {
      const result = await testFn()
      const duration = Date.now() - startTime
      
      setTestResults(prev => [...prev, {
        name: testName,
        status: 'success',
        result,
        duration,
        timestamp: new Date().toLocaleTimeString()
      }])
    } catch (error) {
      const duration = Date.now() - startTime
      
      setTestResults(prev => [...prev, {
        name: testName,
        status: 'error',
        error: error instanceof Error ? error.message : 'Unknown error',
        duration,
        timestamp: new Date().toLocaleTimeString()
      }])
    } finally {
      setIsLoading(false)
    }
  }

  const testCurrentVersion = () => runTest('Current Version API', async () => {
    const response = await fetch('/api/version', { cache: 'no-cache' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return await response.json()
  })

  const testNewVersion = () => runTest('Simulated New Version', async () => {
    const response = await fetch('/api/version-test', { cache: 'no-cache' })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    return await response.json()
  })

  const clearResults = () => setTestResults([])

  const forceReload = () => {
    if (typeof window !== 'undefined') {
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          cacheNames.forEach((cacheName) => {
            caches.delete(cacheName)
          })
        }).finally(() => {
          (window as unknown as { location: { reload: () => void } }).location.reload()
        })
      } else {
        (window as unknown as { location: { reload: () => void } }).location.reload()
      }
    }
  }

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null
  }

  return (
    <Card className="fixed top-4 left-4 w-96 max-h-96 overflow-auto z-50 bg-background/95 backdrop-blur">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm">
          <TestTube className="h-4 w-4" />
          Version Test Panel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Current Version Info */}
        <div className="text-xs space-y-1">
          <div><strong>Current:</strong> v{currentVersion.version}</div>
          <div><strong>Build:</strong> {currentVersion.buildId}</div>
        </div>

        {/* Test Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={testCurrentVersion}
            disabled={isLoading}
          >
            Test Current
          </Button>
          <Button 
            size="sm" 
            variant="outline" 
            onClick={testNewVersion}
            disabled={isLoading}
          >
            Test Update
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button 
            size="sm" 
            variant="destructive" 
            onClick={forceReload}
            className="flex-1"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Force Reload
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={clearResults}
          >
            Clear
          </Button>
        </div>

        {/* Test Results */}
        {testResults.length > 0 && (
          <div className="space-y-2 max-h-32 overflow-auto">
            <div className="text-xs font-medium">Results:</div>
            {testResults.slice(-2).map((test, index) => (
              <div key={index} className="text-xs p-2 rounded border">
                <div className="flex items-center gap-2">
                  {test.status === 'success' ? (
                    <CheckCircle className="h-3 w-3 text-green-500" />
                  ) : (
                    <AlertCircle className="h-3 w-3 text-red-500" />
                  )}
                  <span className="font-medium">{test.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {test.duration}ms
                  </Badge>
                </div>
                {test.status === 'success' && test.result && typeof test.result === 'object' && test.result !== null && 'version' in test.result && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Version: {String((test.result as { version: unknown }).version)}
                  </div>
                )}
                {test.status === 'error' && (
                  <div className="mt-1 text-xs text-red-600">{test.error}</div>
                )}
              </div>
            ))}
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <RefreshCw className="h-3 w-3 animate-spin" />
            Running test...
          </div>
        )}
      </CardContent>
    </Card>
  )
}
